import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import { useIntegratorMe } from "@/lib/me-query";
import {
  assignNotificationItemOwner,
  claimNotificationItemOwner,
  fetchNotificationTopicOwners,
  HUB_NOTIFICATION_TOPIC_ADMIN_MODULE,
  HUB_NOTIFICATION_TOPIC_LABELS,
  notificationsTopicOwnersQueryKey,
  notificationsUnreadCountQueryKey,
  type HubNotificationTopic,
} from "@/lib/notificationsApi";
import { hubModuleAllowsAdminSettings } from "@/lib/subapp-access";

export const NOTIFICATION_ITEM_OWNER_SELECT_CLASS =
  "h-8 w-[11rem] shrink-0 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500";

export const NOTIFICATION_ITEM_TAKE_OVER_BUTTON_CLASS = "relative w-[5.75rem] justify-center";

export type NotificationItemOwnerControlsProps = {
  topic: HubNotificationTopic;
  sourceId: string;
  ownerUserId: string | null;
  ownerDisplayName: string | null;
  disabled?: boolean;
  onOwnershipChanged?: () => void;
};

export function formatNotificationItemOwnerLabel(
  ownerUserId: string | null,
  ownerDisplayName: string | null,
): string {
  if (ownerUserId == null || ownerUserId.trim() === "") {
    return "Unassigned";
  }
  const trimmed = ownerDisplayName?.trim() ?? "";
  return trimmed !== "" ? trimmed : ownerUserId;
}

export function NotificationItemOwnerControls({
  topic,
  sourceId,
  ownerUserId,
  ownerDisplayName,
  disabled = false,
  onOwnershipChanged,
}: NotificationItemOwnerControlsProps): JSX.Element {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const meQuery = useIntegratorMe();
  const currentUserId = meQuery.data?.user.id ?? null;
  const topicLabel = HUB_NOTIFICATION_TOPIC_LABELS[topic];
  const isUnassigned = ownerUserId == null;

  const canAssign = hubModuleAllowsAdminSettings(
    meQuery.data,
    HUB_NOTIFICATION_TOPIC_ADMIN_MODULE[topic],
  );

  const topicOwnersQuery = useQuery({
    queryKey: notificationsTopicOwnersQueryKey(topic),
    queryFn: () => fetchNotificationTopicOwners(topic),
    enabled: currentUserId != null && (canAssign || isUnassigned),
  });

  const canClaim =
    currentUserId != null
    && (topicOwnersQuery.data?.owners.some((owner) => owner.userId === currentUserId) ?? false);
  const isCurrentOwner =
    ownerUserId != null && currentUserId != null && ownerUserId === currentUserId;
  const canTakeOver =
    currentUserId != null
    && !isCurrentOwner
    && !disabled
    && ((isUnassigned && canClaim) || (!isUnassigned && canAssign));
  const showTakeOverButton = currentUserId != null && (canAssign || canClaim);

  function invalidateOwnership(): void {
    void queryClient.invalidateQueries({ queryKey: ["notifications", "list"] });
    void queryClient.invalidateQueries({ queryKey: notificationsUnreadCountQueryKey });
    onOwnershipChanged?.();
  }

  const assignMutation = useMutation({
    mutationFn: (nextOwnerUserId: string) =>
      assignNotificationItemOwner(topic, sourceId, nextOwnerUserId),
    onSuccess: () => {
      invalidateOwnership();
      showToast({ tone: "success", message: "Owner assigned." });
    },
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : String(error),
      });
    },
  });

  const takeOverMutation = useMutation({
    mutationFn: async () => {
      if (isUnassigned) {
        return claimNotificationItemOwner(topic, sourceId);
      }
      return assignNotificationItemOwner(topic, sourceId, currentUserId!);
    },
    onSuccess: () => {
      invalidateOwnership();
      showToast({ tone: "success", message: "You took over ownership." });
    },
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : String(error),
      });
    },
  });

  const busy = assignMutation.isPending || takeOverMutation.isPending;

  const assignOptions = useMemo(() => {
    const owners = topicOwnersQuery.data?.owners ?? [];
    const options = owners.map((owner) => ({
      userId: owner.userId,
      label: owner.displayName?.trim() ? owner.displayName : owner.email,
    }));
    const currentOwnerId = ownerUserId?.trim() ?? "";
    if (
      currentOwnerId !== ""
      && !options.some((option) => option.userId === currentOwnerId)
      && ownerDisplayName
    ) {
      options.unshift({
        userId: currentOwnerId,
        label: ownerDisplayName,
      });
    }
    return options;
  }, [ownerDisplayName, ownerUserId, topicOwnersQuery.data?.owners]);

  if (!canAssign && !showTakeOverButton) {
    return (
      <p className="text-sm text-slate-700">
        {formatNotificationItemOwnerLabel(ownerUserId, ownerDisplayName)}
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {canAssign ? (
        <select
          aria-label={`Reassign ${topicLabel} owner`}
          className={NOTIFICATION_ITEM_OWNER_SELECT_CLASS}
          value={ownerUserId ?? ""}
          disabled={disabled || busy || topicOwnersQuery.isLoading}
          onChange={(event) => {
            const value = event.target.value.trim();
            if (value === "") {
              return;
            }
            assignMutation.mutate(value);
          }}
        >
          <option value="">Unassigned</option>
          {assignOptions.map((option) => (
            <option key={option.userId} value={option.userId}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <p className="text-sm text-slate-700">
          {formatNotificationItemOwnerLabel(ownerUserId, ownerDisplayName)}
        </p>
      )}
      {showTakeOverButton ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className={NOTIFICATION_ITEM_TAKE_OVER_BUTTON_CLASS}
          disabled={
            !canTakeOver
            || isCurrentOwner
            || busy
            || disabled
            || (meQuery.isLoading && currentUserId == null)
          }
          onClick={() => takeOverMutation.mutate()}
        >
          {takeOverMutation.isPending ? (
            <span
              className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-slate-500 border-t-transparent"
              aria-hidden
            />
          ) : (
            "Take over"
          )}
        </Button>
      ) : null}
    </div>
  );
}
